// Assembly catalog (§5.8). Each device type expands into a list of catalog parts
// × per-unit quantities, with material cost (source + date) and NECA MLU / BICSI
// labor unit hours (source + date). This is the seed knowledge base — every value
// is editable/source-stamped (admin UI in Phase 8) and material costs are
// internet-estimated placeholders until distributor pricing lands (Phase 6).
//
// TODO(pricing): replace internet-estimated material_cost with distributor pricing
// via the §5.11 hierarchy. Labor hours are typical NECA Manual of Labor Units
// (electrical/LV) and BICSI (structured cabling) values — confirm per project.

export type LaborCategory = 'electrician' | 'sound_comm' | 'technician';

export interface CatalogPart {
  part_number: string;
  manufacturer: string;
  description: string;
  unit: string;
  material_cost: number;     // internet-estimated USD placeholder
  qty_per: number;           // quantity of this part per one device
  labor_hours: number;       // NECA MLU / BICSI install hours for qty_per
  labor_source: string;      // 'NECA MLU' | 'BICSI' | 'in-house'
}

export interface Assembly {
  device_key: string;        // matches DEVICE_LIBRARY key
  labor_category: LaborCategory;
  parts: CatalogPart[];
}

const COST_SRC = 'internet-estimated 2026-06';
// Compact builder: [part_number, manufacturer, description, unit, material_cost, qty_per, labor_hours, labor_source]
type P = [string, string, string, string, number, number, number, string];
const part = (p: P): CatalogPart => ({
  part_number: p[0], manufacturer: p[1], description: p[2], unit: p[3],
  material_cost: p[4], qty_per: p[5], labor_hours: p[6], labor_source: p[7],
});
const asm = (device_key: string, labor_category: LaborCategory, parts: P[]): Assembly =>
  ({ device_key, labor_category, parts: parts.map(part) });

export const ASSEMBLY_LIBRARY: Assembly[] = [
  // ── Access Control ──
  asm('access.card_reader', 'electrician', [
    ['SR-RDR', 'HID', 'Multiclass SE card reader', 'ea', 185, 1, 0.75, 'NECA MLU'],
    ['BB-1G', 'Generic', 'Single-gang back box', 'ea', 6, 1, 0.20, 'NECA MLU'],
    ['CBL-22-6', 'WestPenn', 'Access control composite cable', 'ft', 0.55, 50, 0.50, 'BICSI'],
    ['TERM-AC', 'Generic', 'Termination & connectors allowance', 'ea', 8, 1, 0.40, 'NECA MLU'],
  ]),
  asm('access.door_contact', 'electrician', [
    ['DC-1085', 'GE/Interlogix', 'Recessed door position switch', 'ea', 18, 1, 0.30, 'NECA MLU'],
    ['CBL-22-2', 'WestPenn', '22/2 lead-in cable', 'ft', 0.28, 25, 0.25, 'BICSI'],
  ]),
  asm('access.rex', 'electrician', [
    ['REX-PIR', 'Bosch', 'Request-to-exit PIR', 'ea', 95, 1, 0.50, 'NECA MLU'],
    ['BB-1G', 'Generic', 'Single-gang back box', 'ea', 6, 1, 0.20, 'NECA MLU'],
    ['CBL-22-4', 'WestPenn', '22/4 cable', 'ft', 0.34, 30, 0.30, 'BICSI'],
  ]),
  asm('access.lock', 'electrician', [
    ['STR-6300', 'HES', 'Electric strike', 'ea', 320, 1, 1.50, 'NECA MLU'],
    ['PT-EPT10', 'Von Duprin', 'Power transfer', 'ea', 140, 1, 0.75, 'NECA MLU'],
    ['CBL-18-4', 'WestPenn', '18/4 lock power cable', 'ft', 0.45, 30, 0.30, 'BICSI'],
  ]),
  asm('access.controller', 'electrician', [
    ['ACU-PRO32', 'Mercury/LenelS2', '2-door controller', 'ea', 650, 1, 2.50, 'NECA MLU'],
    ['ENC-MED', 'Generic', 'Controller enclosure w/ PSU', 'ea', 240, 1, 1.50, 'NECA MLU'],
  ]),
  asm('access.intercom', 'sound_comm', [
    ['IX-DV', 'Aiphone', 'Video door station', 'ea', 420, 1, 1.25, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 station cable', 'ft', 0.30, 100, 0.60, 'BICSI'],
  ]),

  // ── CCTV ──
  asm('cctv.fixed_dome', 'sound_comm', [
    ['CAM-FD', 'Axis', 'Fixed dome IP camera', 'ea', 380, 1, 0.90, 'NECA MLU'],
    ['MNT-DOME', 'Generic', 'Mount / back box', 'ea', 28, 1, 0.30, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop', 'ft', 0.30, 120, 0.70, 'BICSI'],
    ['LIC-VMS', 'Genetec', 'VMS channel license (allowance)', 'ea', 120, 1, 0.10, 'in-house'],
  ]),
  asm('cctv.ptz', 'sound_comm', [
    ['CAM-PTZ', 'Axis', 'PTZ IP camera', 'ea', 1450, 1, 1.50, 'NECA MLU'],
    ['MNT-PTZ', 'Generic', 'Pendant/corner mount', 'ea', 120, 1, 0.75, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop', 'ft', 0.30, 120, 0.70, 'BICSI'],
    ['LIC-VMS', 'Genetec', 'VMS channel license (allowance)', 'ea', 120, 1, 0.10, 'in-house'],
  ]),
  asm('cctv.bullet', 'sound_comm', [
    ['CAM-BL', 'Axis', 'Bullet IP camera', 'ea', 360, 1, 0.90, 'NECA MLU'],
    ['MNT-BL', 'Generic', 'Bullet mount', 'ea', 24, 1, 0.30, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop', 'ft', 0.30, 120, 0.70, 'BICSI'],
    ['LIC-VMS', 'Genetec', 'VMS channel license (allowance)', 'ea', 120, 1, 0.10, 'in-house'],
  ]),
  asm('cctv.multisensor', 'sound_comm', [
    ['CAM-MS', 'Axis', 'Multisensor IP camera', 'ea', 1850, 1, 1.50, 'NECA MLU'],
    ['MNT-DOME', 'Generic', 'Mount / back box', 'ea', 40, 1, 0.40, 'NECA MLU'],
    ['CBL-CAT6', 'WestPenn', 'Cat6 camera drop', 'ft', 0.30, 120, 0.70, 'BICSI'],
    ['LIC-VMS', 'Genetec', 'VMS channel license (allowance)', 'ea', 240, 1, 0.10, 'in-house'],
  ]),

  // ── Intrusion ──
  asm('intrusion.motion', 'electrician', [
    ['ISC-BDL2', 'Bosch', 'Dual-tech motion detector', 'ea', 58, 1, 0.50, 'NECA MLU'],
    ['CBL-22-4', 'WestPenn', '22/4 cable', 'ft', 0.34, 40, 0.35, 'BICSI'],
  ]),
  asm('intrusion.glassbreak', 'electrician', [
    ['DS1101', 'Bosch', 'Glass-break detector', 'ea', 62, 1, 0.50, 'NECA MLU'],
    ['CBL-22-4', 'WestPenn', '22/4 cable', 'ft', 0.34, 40, 0.35, 'BICSI'],
  ]),
  asm('intrusion.keypad', 'electrician', [
    ['B940', 'Bosch', 'Intrusion keypad', 'ea', 145, 1, 0.75, 'NECA MLU'],
    ['CBL-22-6', 'WestPenn', '22/6 cable', 'ft', 0.55, 50, 0.40, 'BICSI'],
  ]),

  // ── Fire Alarm ──
  asm('fa.smoke', 'electrician', [
    ['SD-355', 'Notifier', 'Addressable photoelectric smoke', 'ea', 62, 1, 0.50, 'NECA MLU'],
    ['B210LP', 'Notifier', 'Detector base', 'ea', 14, 1, 0.10, 'NECA MLU'],
    ['FPLP-18-2', 'Genesis', 'FPLP SLC cable', 'ft', 0.42, 40, 0.40, 'NECA MLU'],
  ]),
  asm('fa.heat', 'electrician', [
    ['FST-851', 'Notifier', 'Addressable heat detector', 'ea', 55, 1, 0.50, 'NECA MLU'],
    ['B210LP', 'Notifier', 'Detector base', 'ea', 14, 1, 0.10, 'NECA MLU'],
    ['FPLP-18-2', 'Genesis', 'FPLP SLC cable', 'ft', 0.42, 40, 0.40, 'NECA MLU'],
  ]),
  asm('fa.duct', 'electrician', [
    ['DNR', 'Notifier', 'Duct smoke detector housing', 'ea', 230, 1, 1.25, 'NECA MLU'],
    ['RTS151', 'System Sensor', 'Remote test station', 'ea', 48, 1, 0.40, 'NECA MLU'],
    ['FPLP-18-2', 'Genesis', 'FPLP SLC cable', 'ft', 0.42, 50, 0.45, 'NECA MLU'],
  ]),
  asm('fa.pull', 'electrician', [
    ['NBG-12LX', 'Notifier', 'Addressable manual pull station', 'ea', 58, 1, 0.50, 'NECA MLU'],
    ['BB-1G', 'Generic', 'Single-gang back box', 'ea', 6, 1, 0.20, 'NECA MLU'],
    ['FPLP-18-2', 'Genesis', 'FPLP SLC cable', 'ft', 0.42, 35, 0.35, 'NECA MLU'],
  ]),
  asm('fa.horn_strobe', 'electrician', [
    ['P2RL', 'System Sensor', 'Horn/strobe appliance', 'ea', 64, 1, 0.50, 'NECA MLU'],
    ['BB-4SQ', 'Generic', '4-square box w/ ring', 'ea', 8, 1, 0.20, 'NECA MLU'],
    ['FPLP-16-2', 'Genesis', 'FPLP NAC cable', 'ft', 0.48, 40, 0.40, 'NECA MLU'],
  ]),
  asm('fa.strobe', 'electrician', [
    ['SR', 'System Sensor', 'Strobe appliance', 'ea', 52, 1, 0.50, 'NECA MLU'],
    ['BB-4SQ', 'Generic', '4-square box w/ ring', 'ea', 8, 1, 0.20, 'NECA MLU'],
    ['FPLP-16-2', 'Genesis', 'FPLP NAC cable', 'ft', 0.48, 40, 0.40, 'NECA MLU'],
  ]),
  asm('fa.speaker', 'electrician', [
    ['SPSWL', 'System Sensor', 'Speaker/strobe', 'ea', 88, 1, 0.60, 'NECA MLU'],
    ['BB-4SQ', 'Generic', '4-square box w/ ring', 'ea', 8, 1, 0.20, 'NECA MLU'],
    ['FPLP-16-2', 'Genesis', 'FPLP audio cable', 'ft', 0.48, 45, 0.45, 'NECA MLU'],
  ]),
  asm('fa.module', 'electrician', [
    ['FMM-1', 'Notifier', 'Addressable monitor/control module', 'ea', 46, 1, 0.50, 'NECA MLU'],
    ['BB-4SQ', 'Generic', '4-square box', 'ea', 7, 1, 0.20, 'NECA MLU'],
    ['FPLP-18-2', 'Genesis', 'FPLP SLC cable', 'ft', 0.42, 30, 0.30, 'NECA MLU'],
  ]),
  asm('fa.facp', 'electrician', [
    ['NFS2-3030', 'Notifier', 'Addressable fire alarm control panel', 'ea', 4200, 1, 8.0, 'NECA MLU'],
    ['BATT-12-18', 'Generic', 'Backup batteries (pair)', 'set', 120, 1, 0.50, 'NECA MLU'],
  ]),

  // ── Structured Cabling / Network ──
  asm('cabling.data_drop', 'sound_comm', [
    ['CBL-6A', 'CommScope', 'Cat6A plenum cable', 'ft', 0.42, 120, 0.60, 'BICSI'],
    ['JACK-6A', 'Panduit', 'Cat6A jack', 'ea', 9, 2, 0.20, 'BICSI'],
    ['FP-2', 'Panduit', '2-port faceplate', 'ea', 4, 1, 0.10, 'BICSI'],
    ['TEST-CERT', 'Fluke', 'Certification/test (per drop)', 'ea', 0, 1, 0.15, 'BICSI'],
  ]),
  asm('cabling.wap', 'sound_comm', [
    ['CBL-6A', 'CommScope', 'Cat6A plenum cable', 'ft', 0.42, 150, 0.70, 'BICSI'],
    ['JACK-6A', 'Panduit', 'Cat6A jack', 'ea', 9, 1, 0.15, 'BICSI'],
    ['MNT-WAP', 'Generic', 'WAP mount/back box', 'ea', 14, 1, 0.30, 'BICSI'],
    ['TEST-CERT', 'Fluke', 'Certification/test', 'ea', 0, 1, 0.15, 'BICSI'],
  ]),
  asm('cabling.rack', 'sound_comm', [
    ['RACK-45U', 'CPI', '45U 2-post rack', 'ea', 480, 1, 3.0, 'BICSI'],
    ['PP-48', 'Panduit', '48-port patch panel', 'ea', 140, 2, 1.0, 'BICSI'],
    ['PDU-V', 'TrippLite', 'Vertical PDU', 'ea', 180, 1, 0.5, 'BICSI'],
    ['GND-KIT', 'Panduit', 'Rack ground kit', 'ea', 45, 1, 0.5, 'BICSI'],
  ]),
  asm('cabling.tv', 'sound_comm', [
    ['CBL-RG6', 'CommScope', 'RG6 plenum coax', 'ft', 0.35, 100, 0.50, 'BICSI'],
    ['FP-TV', 'Panduit', 'TV faceplate w/ F-connector', 'ea', 7, 1, 0.20, 'BICSI'],
  ]),

  // ── PA / Nurse Call ──
  asm('pa.speaker', 'sound_comm', [
    ['SPK-8', 'Atlas', 'Ceiling paging speaker w/ back can', 'ea', 42, 1, 0.50, 'NECA MLU'],
    ['CBL-18-2', 'WestPenn', '18/2 speaker cable', 'ft', 0.30, 60, 0.40, 'BICSI'],
  ]),
  asm('nursecall.station', 'sound_comm', [
    ['NC-PS', 'Rauland', 'Patient station', 'ea', 260, 1, 1.0, 'NECA MLU'],
    ['CBL-NC', 'WestPenn', 'Nurse-call system cable', 'ft', 0.65, 80, 0.60, 'BICSI'],
  ]),
];

export const ASSEMBLY_BY_DEVICE: Record<string, Assembly> =
  Object.fromEntries(ASSEMBLY_LIBRARY.map((a) => [a.device_key, a]));
